import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  useAllOrders,
  useRtoAccept,
  useRtoReject,
} from "../../../../api/orders/orders";
import { useLoader } from "../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const RtoStatus = ({ status = "rtodelivered", rtoStatus = "" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [rejectFile, setRejectFile] = useState(null);
  const [rejectShipmentId, setRejectShipmentId] = useState(null);

  const { setLoading } = useLoader();

  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    status,
    rtoStatus,
    search: searchQuery,
  });

  const { mutate: acceptOrder } = useRtoAccept();
  const { mutate: rejectOrder } = useRtoReject();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const filteredOrders = allOrdersData?.orders || [];

  const orders = filteredOrders.filter(order => order.rtoStatus === null);
  const isLoading = isAllLoading;

  const handleAccept = (shipment_id) => {
    acceptOrder({ shipment_id });
  };


  const handleRejectOpen = (shipment_id) => {
    setRejectShipmentId(shipment_id);
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectRemark.trim()) return;

    const payload = {
      shipment_id: rejectShipmentId,
      remark: rejectRemark,
    };
    rejectOrder(payload);

    setRejectModalOpen(false);
    setRejectRemark("");
    setRejectFile(null);
    setRejectShipmentId(null);
  };

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1500px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number</th>
              <th className="p-3">AWB / Courier</th>
              <th className="p-3">Payment / Invoice</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer / Contact</th>

              <th className="p-3">Shipping Address</th>

              <th className="p-3">RTO Info</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={11} className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order._id} className="hover:bg-gray-50">
                  {/* Order Date */}
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  {/* Manifest / Shipping Status */}
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${order.shippingStatusText === "cancelled"
                        ? "bg-red-100 text-red-600"
                        : order.shippingStatusText === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatusText === "shipped"
                            ? "bg-green-100 text-green-600"
                            : "bg-yellow-100 text-yellow-600"
                        }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>

                  {/* AWB / Courier */}
                  <td className="p-4">
                    <p className="font-medium">{order.awbNumber}</p>
                    <p>{order.courier}</p>
                  </td>

                  {/* Payment / Invoice */}
                  <td className="p-4">
                    ₹{order.InvoiceAmount}
                    <div className="text-xs mt-1 bg-green-100 text-green-700 inline-block px-2 py-1 rounded">
                      {order.PaymentType}
                    </div>
                  </td>

                  {/* Products */}
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name} (Unites: {prod.units})
                      </p>
                    ))}
                  </td>

                  {/* Customer / Contact */}
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>

                  {/* Shipping Address */}
                  <td className="p-4 text-sm">
                    <p>{order.ShippingAddress?.AddressLine1}</p>
                    {order.ShippingAddress?.AddressLine2 && (
                      <p>{order.ShippingAddress.AddressLine2}</p>
                    )}
                    <p>
                      {order.ShippingAddress?.City},{" "}
                      {order.ShippingAddress?.State} -{" "}
                      {order.ShippingAddress?.Pincode}
                    </p>
                    <p>{order.ShippingAddress?.Country}</p>
                  </td>

                  {/* RTO Info */}
                  <td className="p-4 text-sm">
                    {order.rtoCaption && (
                      <p>
                        <strong>Caption:</strong> {order.rtoCaption}
                      </p>
                    )}
                    {order.rtoPhoto && (
                      <p>
                        <strong>Photo:</strong>{" "}
                        <a
                          href={order.rtoPhoto}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      </p>
                    )}
                    {order.rtoRemark && (
                      <p>
                        <strong>Remark:</strong> {order.rtoRemark}
                      </p>
                    )}
                    {!order.rtoCaption &&
                      !order.rtoPhoto &&
                      !order.rtoRemark && (
                        <span className="text-gray-400 text-xs">
                          No RTO info
                        </span>
                      )}
                  </td>

                  {/* Actions */}
                  <td className="p-4 space-x-2">
                    {["accepted", "rejected"].includes(order.rtoStatus) ? (
                      <span
                        className={`text-xs font-semibold ${order.rtoStatus === "accepted" ? "text-green-600" : "text-red-600"
                          }`}
                      >
                        {order.rtoStatus}
                      </span>
                    ) : (
                      <>
                        <button
                          onClick={() => handleAccept(order.shiprocket_order_id)}
                          className="px-3 py-1 rounded-lg bg-primaryLight hover:bg-primary text-primary border border-primary hover:text-white transition-colors"
                        >
                          Accept
                        </button>

                        <button
                          onClick={() => handleRejectOpen(order.shiprocket_order_id)}
                          className="px-3 py-1 rounded-lg text-red-600 border border-red-600 bg-primaryLight hover:bg-red-600 hover:text-white hover:border-red-700 transition-colors"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </td>




                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={11} className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Reject Modal */}
      <Modal
        title="Reject Order"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setRejectModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="reject"
            danger
            type="primary"
            onClick={handleRejectConfirm}
          >
            Reject
          </Button>,
        ]}
      >
        <p className="mb-2">Enter reason for rejection:</p>
        <Input.TextArea
          rows={4}
          value={rejectRemark}
          onChange={(e) => setRejectRemark(e.target.value)}
          placeholder="Enter rejection remark..."
        />
        <div className="mt-4">
          {/* <Upload
            beforeUpload={(file) => {
              setRejectFile(file);
              return false;
            }}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Upload Photo</Button>
          </Upload> */}
          {rejectFile && (
            <p className="mt-2 text-sm text-gray-600">
              Selected file: {rejectFile.name}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RtoStatus;
